import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Zap, ArrowLeft, ArrowRight, UserCog, Users, Building2, Activity,
  Check, Shield, Brain, Sparkles
} from 'lucide-react';

// Tailwind JIT safelist (used dynamically via bg-${accent}/10 etc.):
// bg-orange-400/10 border-orange-400/20 text-orange-400 bg-orange-400/15 border-orange-400/30 text-orange-400/80

const PATHS = [
  {
    key: 'coach',
    title: 'Private Coach',
    tagline: 'Build your coaching business with structured development.',
    icon: UserCog,
    accent: 'primary',
    accentHex: 'rgb(234 179 8)',
    href: '/coach/signup',
    cta: 'Create Coach Account',
    type: 'Direct signup',
    bullets: [
      'Athlete management & evaluations',
      'Training plans + Drill library (60+)',
      'Video analysis & parent communication',
      'Athlete Pass program — earn FREE access',
    ],
  },
  {
    key: 'athlete',
    title: 'Player & Family',
    tagline: 'See real development — beyond attendance.',
    icon: Users,
    accent: 'emerald-400',
    accentHex: 'rgb(52 211 153)',
    href: '/athlete/signup',
    cta: 'Create Athlete Account',
    type: 'Direct signup',
    highlight: true,
    bullets: [
      'AI-powered Development Profile',
      'Skills radar across 7 areas',
      'Train at Home guided sessions',
      'Direct messaging with coaches',
    ],
  },
  {
    key: 'performance',
    title: 'Performance Coach',
    tagline: 'Physical specialist — own the athlete\u2019s explosiveness.',
    icon: Activity,
    accent: 'orange-400',
    accentHex: 'rgb(251 146 60)',
    href: '/coach/signup?role=performance',
    cta: 'Create Performance Account',
    type: 'Direct signup',
    bullets: [
      'Physical evaluation suite (7 attributes)',
      'Dedicated Physical Radar',
      'AI-generated physical narratives',
      'Contributes to the unified athlete profile',
    ],
  },
  {
    key: 'club',
    title: 'Club / Academy',
    tagline: 'Operate your academy at scale with intelligence.',
    icon: Building2,
    accent: 'blue-400',
    accentHex: 'rgb(96 165 250)',
    href: '/club-pilot',
    cta: 'Request Club Pilot',
    type: 'Pilot onboarding · adds Club Coaches internally',
    bullets: [
      'Multi-team management (U8 → Senior)',
      'Performance Intelligence dashboard',
      'Match Reports + AI Decision Engine',
      'Club Admin invites Club Coaches inside the workspace',
    ],
  },
];

const GetStartedPage = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white flex flex-col">
      {/* Header */}
      <header className="border-b border-border/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="getstarted-nav-home">
            <Zap className="text-primary fill-primary" size={26} strokeWidth={2.5} />
            <span className="font-heading text-2xl tracking-wider text-white uppercase">MEYBRA</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors"
            data-testid="getstarted-back-link"
          >
            <ArrowLeft size={14} /> Back
          </Link>
        </div>
      </header>

      <main className="flex-1 py-12 sm:py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12 sm:mb-14"
          >
            <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 rounded-full px-3 py-1 mb-5">
              <Sparkles size={12} className="text-primary" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-primary">Get Started</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl uppercase tracking-wide leading-tight">
              How will you use <span className="text-primary">Meybra</span>?
            </h1>
            <p className="mt-4 text-sm sm:text-base text-muted-foreground max-w-xl mx-auto">
              Choose your path — we will tailor the experience to fit how you develop.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PATHS.map((path, i) => (
              <motion.div
                key={path.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                <Link
                  to={path.href}
                  className={`group relative block bg-card border rounded-sm p-7 h-full flex flex-col transition-all hover:scale-[1.02] hover:shadow-2xl ${
                    path.highlight
                      ? 'border-primary/40 shadow-lg shadow-primary/5'
                      : 'border-border/30 hover:border-primary/30'
                  }`}
                  data-testid={`getstarted-path-${path.key}`}
                >
                  {path.highlight && (
                    <p className="text-[9px] uppercase tracking-widest text-primary mb-4 font-bold">Most Popular</p>
                  )}

                  <div
                    className={`w-14 h-14 rounded-sm bg-${path.accent}/10 border border-${path.accent}/20 flex items-center justify-center mb-5`}
                  >
                    <path.icon size={26} className={`text-${path.accent}`} />
                  </div>

                  <h2 className="font-heading text-xl uppercase tracking-wide mb-1.5">{path.title}</h2>
                  <p className="text-xs text-muted-foreground mb-5 leading-relaxed">{path.tagline}</p>

                  <ul className="space-y-2 flex-1 mb-6">
                    {path.bullets.map((b, bi) => (
                      <li key={bi} className="flex items-start gap-2 text-[13px] text-muted-foreground leading-relaxed">
                        <Check size={12} className={`text-${path.accent} shrink-0 mt-1`} /> {b}
                      </li>
                    ))}
                  </ul>

                  <div className="space-y-3">
                    <p className={`text-[10px] uppercase tracking-widest text-${path.accent}/80 font-semibold`}>
                      {path.type}
                    </p>
                    <div
                      className={`flex items-center justify-between gap-3 px-4 py-3 rounded-sm font-bold text-sm uppercase tracking-wider transition-all ${
                        path.highlight
                          ? 'bg-primary text-black group-hover:bg-primary/90'
                          : `bg-${path.accent}/10 text-${path.accent} border border-${path.accent}/30 group-hover:bg-${path.accent}/15`
                      }`}
                    >
                      <span>{path.cta}</span>
                      <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Trust + Login link */}
          <div className="mt-12 text-center space-y-5">
            <div className="flex flex-wrap items-center justify-center gap-5 text-[10px] uppercase tracking-widest text-zinc-600">
              <span className="flex items-center gap-1.5"><Shield size={11} className="text-primary/50" /> Founding Beta</span>
              <span className="flex items-center gap-1.5"><Brain size={11} className="text-primary/50" /> AI-Powered</span>
              <span className="flex items-center gap-1.5"><Check size={11} className="text-primary/50" /> No credit card required</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Already have an account?{' '}
              <Link to="/unified-login" className="text-primary hover:underline" data-testid="getstarted-login-link">
                Login here
              </Link>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default GetStartedPage;
